// Шаблони документів, крім договору: специфікація/завдання, рахунок, видаткова, акт, ТТН. Шаблон — типові значення й
// тексти документа (не позиції, не сторони, не номер і дата): з них складається новий документ. Підстановки в текстах —
// {{номер}}, {{дата}}, {{договір}}, {{рахунок}}, {{покупець}}, {{постачальник}} — заповнюються при створенні документа;
// суми {{сума}} і {{пдв}} — при друці (позиції можуть змінитися після створення).
// Форма 1-ТН регламентована, тож у шаблоні ТТН — лише перевізник, водій, транспорт і пункт навантаження.
import { specDefaults } from "./spec.js";
import { ttnDefaults } from "./ttn.js";

export const TEMPLATE_DOC_TYPES = { contract: "Договори", spec: "Специфікації й завдання", invoice: "Рахунки на оплату", waybill: "Видаткові накладні", act: "Акти наданих послуг", ttn: "ТТН" };

// Поля знімка документа, які зберігає шаблон
export const TEMPLATE_FIELDS = {
  invoice: ["place", "basis", "paymentTerms", "dueDays", "paymentPurpose", "remarks", "signerLabel", "linesView", "layout"],
  waybill: ["place", "basis", "transferPlace", "intro", "accompanying", "remarks", "receiverAuthority", "linesView", "layout"],
  act: ["place", "basis", "intro", "result", "remarks"],
  spec: ["template", "condition", "docs", "packaging", "batches", "delivery", "partial", "warranty", "payments", "model", "included", "excluded", "format",
    "deliverables", "customerProvides", "criteria", "hourly", "warrantyText", "ip", "personalData", "subcontractors", "edits"],
  ttn: ["carrier", "driver", "vehicle", "trailer", "transportKind", "storageAddress", "loadPlace", "senderPerson"],
};

// Текст перед таблицею видаткової й акта — типовий, якщо шаблон не задає свого
export const DEFAULT_INTRO = {
  waybill: "Постачальник передав, а Покупець прийняв наведені нижче товари:",
  act: "Виконавець надав, а Замовник прийняв наведені нижче послуги у зазначеному фактичному обсязі:",
};

// Готові зразки eCabinet (стандартні зразки рахунку й видаткової): встановлюються як власний шаблон компанії
export const SAMPLES = {
  invoice: { name: "Рахунок — стандартний зразок", note: "умова оплати (100% передоплата за 5 робочих днів), «Всього найменувань», ПДВ у призначенні платежу, «Виписав», таблиця без ПДВ у рядках",
    fields: { place: "", basis: "", paymentTerms: "100% попередня оплата протягом 5 робочих днів від дати рахунку.", dueDays: "",
      paymentPurpose: "Оплата за товар згідно з рахунком №{{номер}} від {{дата}}, у т.ч. ПДВ {{пдв}}", remarks: "", signerLabel: "Виписав", linesView: "net", layout: "sample" } },
  waybill: { name: "Видаткова — стандартний зразок", note: "підстава — договір і рахунок, місце передачі, супровідний документ (ТТН), підстава повноважень одержувача, таблиця без ПДВ у рядках",
    fields: { place: "", basis: "{{рахунок}}", transferPlace: "", intro: "", accompanying: "", remarks: "", receiverAuthority: "", linesView: "net", layout: "sample" } },
};

const clean = (v, depth = 0) => {
  if (typeof v === "string") return v.slice(0, 6000);
  if (typeof v === "number" || typeof v === "boolean" || v == null) return v;
  if (depth > 5) return null;
  if (Array.isArray(v)) return v.slice(0, 100).map((x) => clean(x, depth + 1));
  if (typeof v === "object") return Object.fromEntries(Object.entries(v).slice(0, 200).map(([k, x]) => [String(k).slice(0, 60), clean(x, depth + 1)]));
  return null;
};

// Поля шаблону з довільного об'єкта (знімок документа чи тіло запиту): лише дозволені, з обмеженням розміру
export function templateFields(docType, src = {}) {
  const out = {};
  for (const k of TEMPLATE_FIELDS[docType] || []) if (src[k] !== undefined) out[k] = clean(src[k]);
  if (docType === "ttn" && out.vehicle) out.vehicle = { ...out.vehicle, grossWithCargoKg: "" };
  if (docType === "invoice" && out.dueDays !== undefined) out.dueDays = /^\d{1,3}$/.test(String(out.dueDays)) ? String(Number(out.dueDays)) : "";
  return out;
}

// Початкові поля нового шаблону: specKind — goods|services
export function templateBase(docType, specKind = "goods") {
  if (docType === "spec") return templateFields("spec", specDefaults(specKind === "services" ? "services" : "goods"));
  if (docType === "ttn") return templateFields("ttn", ttnDefaults());
  return Object.fromEntries(TEMPLATE_FIELDS[docType].map((k) => [k, ""]));
}

const addDays = (iso, n) => { const d = new Date(`${iso}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };

// Застосувати шаблон до нового знімка. Порожні рядки шаблону не перезаписують підставлене з договору. → помилка або null
export function applyTemplate(snap, docType, fields = {}) {
  const f = templateFields(docType, fields);
  if (docType === "spec" && f.template && f.template !== snap.template)
    return `Шаблон для ${f.template === "services" ? "послуг" : "товарів"}, а договір — ${snap.template === "services" ? "послуги" : "товари"}`;
  for (const [k, v] of Object.entries(f)) {
    if (k === "dueDays") { if (v !== "" && /^\d{4}-\d{2}-\d{2}$/.test(snap.date || "")) snap.dueDate = addDays(snap.date, Number(v)); continue; }
    if (typeof v === "string" && !v.trim() && String(snap[k] ?? "").trim()) continue;
    snap[k] = v;
  }
  if (docType === "ttn") delete snap.prefilled;
  return null;
}

// Підстановки в текстах документа — після того, як відомі номер і дата
export function fillTemplateText(snap) {
  const day = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join(".") : "");
  const c = snap.contract || {};
  const inv = snap.invoiceRef;
  const v = { "номер": snap.number || "", "дата": day(snap.date), "договір": c.number ? `Договір № ${c.number}${c.date ? ` від ${day(c.date)}` : ""}` : "",
    "рахунок": inv?.number ? `рахунок № ${inv.number}${inv.date ? ` від ${day(inv.date)}` : ""}` : "",
    "покупець": snap.buyer?.name || "", "постачальник": snap.seller?.name || "" };
  const fill = (t) => String(t).replace(/\{\{\s*([\p{L}\d_]+)\s*\}\}/gu, (m, k) => (k in v ? v[k] : m));
  for (const k of ["basis", "paymentTerms", "paymentPurpose", "remarks", "intro", "result", "transferPlace", "accompanying", "receiverAuthority"])
    if (typeof snap[k] === "string") snap[k] = fill(snap[k]).replace(/^[\s;,]+|[\s;,]+$/g, "");
  return snap;
}

// Суми в тексті при друці: {{сума}} — загальна з ПДВ, {{пдв}} — сума ПДВ (з «грн»)
export function fillAmounts(text, { total, vat }) {
  return String(text || "").replace(/\{\{\s*сума\s*\}\}/gu, `${total} грн`).replace(/\{\{\s*пдв\s*\}\}/gu, `${vat} грн`);
}
